import Security from '@mui/icons-material/Security';
import { Box, Divider, Stack } from '@mui/material';
import { getRequestEvaluations } from 'lib/api/decisionEngine';
import {
  ContentCard,
  IconTitleCard,
  IconTitleCardSkeleton,
  MobileBody,
  MobileBody2,
  StatusChip,
} from 'lib/mobileDesignSystem';
import type { Evaluation } from 'lib/types/models';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useFactoringOperationDetail } from '../context';

export interface RiskProps {
  requestId: string;
}

const RISK_DESCRIPTION = 'Evaluación de Riesgo';

const RISK_SKELETON_COUNT = 2;

const LABEL_EVALUACIONES = 'Evaluaciones';
const LABEL_ULTIMO_EVALUADOR = 'Último evaluador';

function getStatusColor(
  statusName: string,
): 'success' | 'error' | 'warning' | 'default' {
  switch (statusName) {
    case 'Aprobado':
      return 'success';
    case 'Rechazado':
      return 'error';
    default:
      return 'warning';
  }
}

const REQUEST_TYPES = [
  'factoring-request',
  'factoring-request-payers',
] as const;

const Risk: React.FC<RiskProps> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { quoteId, requestId } = useFactoringOperationDetail();

  const requestIdNum =
    requestId != null && requestId !== '' ? Number(requestId) : null;

  const evaluationsQueries = [
    getRequestEvaluations(dispatch, {
      requestType: REQUEST_TYPES[0],
      requestId: quoteId,
    }),
    getRequestEvaluations(dispatch, {
      requestType: REQUEST_TYPES[1],
      requestId: requestIdNum,
    }),
  ];

  const evaluationLists = evaluationsQueries.flatMap((query) =>
    Object.values(query.data ?? {}),
  );
  const isPending = evaluationsQueries.some((q) => q.isPending);
  const hasEvaluations = evaluationLists.some((list) => list.length > 0);

  return (
    <Stack spacing={2} sx={{ pt: 2 }}>
      {isPending ? (
        <>
          {Array.from({ length: RISK_SKELETON_COUNT }, (_, i) => (
            <ContentCard key={`skeleton-${i}`}>
              <Stack spacing={0}>
                <IconTitleCardSkeleton />
              </Stack>
            </ContentCard>
          ))}
        </>
      ) : !hasEvaluations ? (
        <MobileBody color="text.secondary">
          No hay evaluaciones de riesgo para esta operación.
        </MobileBody>
      ) : (
        <Stack spacing={2}>
          {evaluationLists.map((riskData: Evaluation[], index: number) => {
            if (riskData.length === 0) return null;
            const first = riskData[0];
            return (
              <Box
                key={`${first.id}-${index}`}
                component="button"
                type="button"
                onClick={() =>
                  navigate(
                    `/finance/factoring/${requestId ?? ''}/evaluation/${first.id}/nodes`,
                    { state: { evaluation: first, quoteId } },
                  )
                }
                sx={{
                  cursor: 'pointer',
                  border: 'none',
                  padding: 0,
                  margin: 0,
                  width: '100%',
                  textAlign: 'left',
                  background: 'none',
                }}
              >
                <ContentCard>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <StatusChip
                        text={first.statusNode.name}
                        type={getStatusColor(first.statusNode.name)}
                      />
                    </Box>
                    <Box>
                      <IconTitleCard
                        icon={<Security fontSize="small" />}
                        title={first.label}
                        description={RISK_DESCRIPTION}
                        sx={{ px: 1.5, py: 1.25 }}
                      />
                    </Box>
                    <Divider orientation="horizontal" flexItem sx={{ my: 1 }} />
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 7,
                      }}
                    >
                      <Stack direction="column" spacing={0.25}>
                        <MobileBody2
                          component="span"
                          sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                        >
                          {LABEL_ULTIMO_EVALUADOR}:
                        </MobileBody2>
                        <MobileBody2 component="span">
                          {first.reviewerName}
                        </MobileBody2>
                      </Stack>
                      <Stack direction="column" spacing={0.25}>
                        <MobileBody2
                          component="span"
                          sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
                        >
                          {LABEL_EVALUACIONES}:
                        </MobileBody2>
                        <MobileBody2 component="span">
                          {riskData.length}
                        </MobileBody2>
                      </Stack>
                    </Box>
                  </Stack>
                </ContentCard>
              </Box>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

export default Risk;
